/**
 * Emits onboarding analytics events.
 *
 * Why a wrapper instead of calling gtag directly:
 *   - The design doc requires consistent event shapes across four event types
 *     (native_pick, native_change, geo_mismatch_modal, other_submitted), and
 *     a Day-0 spike acknowledged that the underlying transport may need to
 *     change. Centralizing means swapping transports is a one-file change.
 *   - Eats errors so a failed analytics call cannot break onboarding.
 *
 * The transport itself (safeGtag/emit) now lives in analyticsTransport.js: a
 * second event family (useUpsellEvents.js) arrived, and keeping the one-file
 * promise above true meant moving the transport rather than copying it. What
 * stays here is this namespace's event names and payload shapes.
 */
import { emit } from "@/composables/analyticsTransport.js";

const EVENT_NAMES = Object.freeze({
  NATIVE_PICK: "onboarding_native_pick",
  NATIVE_CHANGE: "onboarding_native_change",
  GEO_MISMATCH_MODAL: "onboarding_geo_mismatch_modal",
  OTHER_SUBMITTED: "onboarding_other_submitted",
  PLAN_VIEWED: "onboarding_plan_viewed",
  PLAN_SELECTED: "onboarding_plan_selected",
});

export function useOnboardingEvents() {
  return {
    trackNativePick(payload) {
      emit(EVENT_NAMES.NATIVE_PICK, {
        user_id: payload.userId || "",
        geo_country: (payload.geoCountry || "").toUpperCase(),
        geo_source: payload.geoSource || "unknown",
        ip_locale: (payload.ipLocale || "").toLowerCase(),
        picked_lang: (payload.pickedLang || "").toLowerCase(),
        position_in_grid: payload.positionInGrid ?? -1,
        time_to_pick_ms: payload.timeToPickMs ?? -1,
      });
    },

    trackNativeChange(payload) {
      emit(EVENT_NAMES.NATIVE_CHANGE, {
        user_id: payload.userId || "",
        from_lang: (payload.fromLang || "").toLowerCase(),
        to_lang: (payload.toLang || "").toLowerCase(),
        trigger: payload.trigger || "manual_tap",
      });
    },

    trackGeoMismatchModal(payload) {
      emit(EVENT_NAMES.GEO_MISMATCH_MODAL, {
        user_id: payload.userId || "",
        geo: (payload.geo || "").toUpperCase(),
        picked: (payload.picked || "").toLowerCase(),
        suggested: (payload.suggested || "").toLowerCase(),
        outcome: payload.outcome || "dismissed_via_close_button",
        time_on_modal_ms: payload.timeOnModalMs ?? -1,
      });
    },

    trackOtherSubmitted(payload) {
      emit(EVENT_NAMES.OTHER_SUBMITTED, {
        user_id: payload.userId || "",
        typed_language: (payload.typedLanguage || "").slice(0, 100),
        geo: (payload.geo || "").toUpperCase(),
        is_retry: Boolean(payload.isRetry),
      });
    },

    // The tier-selection step reached the user. Paired with trackPlanSelected
    // this is the whole funnel for the step, so it carries no dimensions of
    // its own beyond the user.
    trackPlanViewed(payload) {
      emit(EVENT_NAMES.PLAN_VIEWED, {
        user_id: payload.userId || "",
      });
    },

    // The plan the user confirmed. `plan` is the canonical id (free |
    // foundation | fluency), lower-cased like every other enum-ish param
    // here, and it records an INTENT: a paid pick still has to survive
    // checkout before it means anything.
    trackPlanSelected(payload) {
      emit(EVENT_NAMES.PLAN_SELECTED, {
        user_id: payload.userId || "",
        plan: (payload.plan || "").toLowerCase(),
      });
    },
  };
}

export { EVENT_NAMES };
